import { DialogActions, DialogContent, Stack } from '@mui/material';
import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { BodyLoader, SuspenseWithSentryErrorBoundary } from '@retail/components';
import { OrgUnitType } from '@retail/mgpris/config-types';
import { AddItemsTabs, ImportStateValidation } from '@retail/my-assortment/components';
import {
  AddItemsTab,
  AssortmentItemMinimal,
  AssortmentItemUpload,
} from '@retail/my-assortment/types';
import { UploadContainer } from '@retail/products/components';
import { MgSupplierItemSearchContainer } from '@retail/products/containers';
import { CentralAssortmentMemberView } from '../CentralAssortments';
import { MgSupplierItemSearchResult } from '@retail/products/types';
import { ContainedButton, TextButton } from '@shared/components';
import { useAppTFunction } from '@retail/app/i18n';
import { useImportAssortmentItemsStore } from '@retail/my-assortment/stores';
import { downloadTemplateCsv } from '@retail/products/utils';

const csvDataNO =
  'MgItem-nummer;Gtin-nummer;NOBB-nummer;MgSupplier-id;Prioritized;Lagerstatus(L/B);Valid from;Valid to';
const csvDataSE =
  'MgItem-nummer;Gtin-nummer;Finfo-nummer;MgSupplier-id;Prioritized;Lagerstatus(L/B);Valid from;Valid to';
interface Props {
  selectedSearchItems: MgSupplierItemSearchResult[];
  selectedCentralItems: AssortmentItemMinimal[];
  importedItems: AssortmentItemUpload[];
  selectedTab: AddItemsTab;
  setSelectedTab: (tab: AddItemsTab) => void;
  setSelectedSearchItems: (items: MgSupplierItemSearchResult[]) => void;
  setSelectedCentralItems: (items: AssortmentItemMinimal[]) => void;
  onClose: () => void;
  onContinue: () => void;
  loadingValidateItems: boolean;
  failedValidation: boolean;
}

export function AddItemsDialogContent({
  selectedSearchItems,
  selectedCentralItems,
  importedItems,
  selectedTab,
  setSelectedTab,
  setSelectedSearchItems,
  setSelectedCentralItems,
  onClose,
  onContinue,
  loadingValidateItems,
  failedValidation,
}: Props) {
  const t = useAppTFunction();
  const { assortmentId, orgUnitType, appTenant, isBrandContext } = useSelectedOrgUnit();

  const csvData = appTenant.countryCode === 'NO' ? csvDataNO : csvDataSE;

  const {
    importedFile,
    importedAssortmentItems,
    invalidItemsState,
    importSpreadsheet,
    clearState,
  } = useImportAssortmentItemsStore();

  const includeCentralAssortments = orgUnitType.baseType === OrgUnitType.RETAIL_LOCATION;

  const importItemsDisabled =
    (selectedTab === 'CENTRAL_ASSORTMENTS' && selectedCentralItems.length === 0) ||
    (selectedTab === 'ITEM_SEARCH' && selectedSearchItems.length === 0) ||
    (selectedTab === 'IMPORT' &&
      (importedItems.length === 0 || loadingValidateItems || failedValidation));

  return (
    <>
      <DialogContent
        className="padding-0"
        sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}
      >
        <Stack px={3}>
          <AddItemsTabs
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            includeCentralAssortments={includeCentralAssortments}
          />
        </Stack>

        {selectedTab === 'ITEM_SEARCH' && (
          <SuspenseWithSentryErrorBoundary>
            <MgSupplierItemSearchContainer
              assortmentId={assortmentId}
              checkedItems={selectedSearchItems}
              setCheckedItems={setSelectedSearchItems}
            />
          </SuspenseWithSentryErrorBoundary>
        )}

        {selectedTab === 'IMPORT' && (
          <Stack flexGrow={1} justifyContent="center">
            <UploadContainer
              isLarge
              importedFile={importedFile}
              onDownloadTemplate={() =>
                downloadTemplateCsv(
                  isBrandContext ? `${csvData};Sub-Assortments` : csvData,
                  'addMgItemsTemplate.csv'
                )
              }
              resetImportedStates={clearState}
              importSpreadsheet={importSpreadsheet}
              renderValidation={
                loadingValidateItems ? (
                  <BodyLoader />
                ) : (
                  <ImportStateValidation
                    invalidItemsState={invalidItemsState ?? []}
                    totalItemsImported={importedAssortmentItems?.length ?? 0}
                    failedValidation={failedValidation}
                    countryCode={appTenant.countryCode}
                  />
                )
              }
            />
          </Stack>
        )}
        {selectedTab === 'CENTRAL_ASSORTMENTS' && (
          <SuspenseWithSentryErrorBoundary>
            <CentralAssortmentMemberView
              includeTitle={false}
              hideActionToolbar
              itemsChecked={selectedCentralItems}
              setItemsChecked={setSelectedCentralItems}
            />
          </SuspenseWithSentryErrorBoundary>
        )}
      </DialogContent>

      <DialogActions>
        <Stack direction="row" spacing={1}>
          <TextButton onClick={onClose}>{t('products.cancel')}</TextButton>

          <ContainedButton
            color="secondary"
            disabled={importItemsDisabled}
            size="small"
            onClick={onContinue}
          >
            {selectedTab === 'IMPORT'
              ? t('myAssortment.actions.continueWithApprovedItems')
              : t('myAssortment.actions.continueWithItems')}
          </ContainedButton>
        </Stack>
      </DialogActions>
    </>
  );
}
